import { useMutation, useQueryClient } from '@tanstack/react-query';

import {
	bulkCreateActivities,
	bulkCreateActivitiesByCompanies,
	createActivity,
	updateActivity,
	completeActivity,
	deleteActivity,
} from '../../api/crmApi';
import { dealsKeys } from '../deals/keys';

import { activitiesKeys, activitiesMutationKeys } from './keys';

// Centralized invalidation for activity changes — refreshes all related caches
// (activity lists, deal detail/next-action, company activities/page, kanban cards)
function invalidateAfterActivityChange(qc, dealId, companyId) {
	qc.invalidateQueries({ queryKey: activitiesKeys.all });
	if (dealId) {
		qc.invalidateQueries({ queryKey: activitiesKeys.byDeal(dealId) });
		qc.invalidateQueries({ queryKey: dealsKeys.detail(dealId) });
		qc.invalidateQueries({ queryKey: ['crm', 'nextAction', dealId] });
		//Invalidate deals view to see current activity cards
		qc.invalidateQueries({ queryKey: ['crm', 'deals', 'byStageInfinite'] });
	}
	if (companyId) {
		qc.invalidateQueries({ queryKey: activitiesKeys.byCompany(companyId) });
		qc.invalidateQueries({ queryKey: ['crm', 'company', companyId] });
	}
}

export function useCreateActivity() {
	const qc = useQueryClient();
	return useMutation({
		mutationKey: activitiesMutationKeys.create,
		mutationFn: createActivity,
		onSuccess: (data) => invalidateAfterActivityChange(qc, data?.activity?.dealId, data?.activity?.crmCompanyId),
	});
}

/**
 * Bulk-create activities across multiple deals. Invalidates broadly because
 * the response intentionally omits per-deal data — too many entries to patch
 * surgically. The one-shot invalidation refetches lazily on next render.
 */
export function useBulkCreateActivities() {
	const qc = useQueryClient();
	return useMutation({
		mutationKey: activitiesMutationKeys.bulkCreateByDeals,
		mutationFn: bulkCreateActivities,
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: activitiesKeys.all });
			qc.invalidateQueries({ queryKey: dealsKeys.all });
		},
	});
}

/**
 * Bulk-create one company-bound activity per company. Mirrors
 * useBulkCreateActivities but targets companies (no deal link), so it
 * invalidates activities and companies rather than deals.
 */
export function useBulkCreateActivitiesByCompanies() {
	const qc = useQueryClient();
	return useMutation({
		mutationKey: activitiesMutationKeys.bulkCreateByCompanies,
		mutationFn: bulkCreateActivitiesByCompanies,
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: activitiesKeys.all });
			qc.invalidateQueries({ queryKey: ['crm', 'company'] });
		},
	});
}

export function useUpdateActivity() {
	const qc = useQueryClient();
	return useMutation({
		mutationKey: activitiesMutationKeys.update,
		mutationFn: ({ id, payload }) => updateActivity(id, payload),
		onSuccess: (data) => invalidateAfterActivityChange(qc, data?.activity?.dealId, data?.activity?.crmCompanyId),
	});
}

export function useCompleteActivity() {
	const qc = useQueryClient();
	return useMutation({
		mutationKey: activitiesMutationKeys.complete,
		mutationFn: ({ id, payload = {} }) => completeActivity(id, payload),
		// Optimistic update (deal-scoped only)
		onMutate: async ({ id, dealId }) => {
			if (!dealId) return {};
			const activityQueryKey = activitiesKeys.byDeal(dealId);
			await qc.cancelQueries({ queryKey: activityQueryKey });

			const previousActivities = qc.getQueryData(activityQueryKey);

			// Determine if the activity was overdue — needed for accurate badge decrement.
			// plannedActivityCount counts every open activity; overdueActivityCount is
			// a subset (also incremented when dueAt is in the past).
			const wasOverdue = Array.isArray(previousActivities)
				? (() => {
						const a = previousActivities.find((x) => x.id === id);
						return Boolean(a?.dueAt && new Date(a.dueAt).getTime() < Date.now());
					})()
				: false;

			qc.setQueryData(activityQueryKey, (old) => {
				if (!old || !Array.isArray(old)) return old;
				return old.map((a) =>
					a.id === id ? { ...a, status: 'completed', completedAt: new Date().toISOString() } : a,
				);
			});

			// Patch any cached deals (kanban stage pages) so badges decrement immediately.
			const stageQueries = qc.getQueriesData({ queryKey: ['crm', 'deals', 'byStageInfinite'] });
			const previousStageData = [];
			for (const [key, data] of stageQueries) {
				if (!data?.pages) continue;
				let touched = false;
				const nextData = {
					...data,
					pages: data.pages.map((page) => ({
						...page,
						deals: page.deals.map((deal) => {
							if (deal.id !== dealId) return deal;
							touched = true;
							const planned = Math.max(0, (deal.plannedActivityCount ?? 0) - 1);
							const overdue = wasOverdue
								? Math.max(0, (deal.overdueActivityCount ?? 0) - 1)
								: (deal.overdueActivityCount ?? 0);
							return { ...deal, plannedActivityCount: planned, overdueActivityCount: overdue };
						}),
					})),
				};
				if (touched) {
					previousStageData.push([key, data]);
					qc.setQueryData(key, nextData);
				}
			}

			return { previousActivities, activityQueryKey, previousStageData };
		},
		onError: (err, vars, context) => {
			if (context?.previousActivities) {
				qc.setQueryData(context.activityQueryKey, context.previousActivities);
			}
			if (context?.previousStageData) {
				for (const [key, data] of context.previousStageData) {
					qc.setQueryData(key, data);
				}
			}
		},
		onSettled: (data) => {
			invalidateAfterActivityChange(qc, data?.activity?.dealId, data?.activity?.crmCompanyId);
		},
	});
}

export function useDeleteActivity(dealId, crmCompanyId) {
	const qc = useQueryClient();
	return useMutation({
		mutationKey: activitiesMutationKeys.delete,
		mutationFn: deleteActivity,
		// Invalidate activity lists + related deal/company caches
		onSettled: () => invalidateAfterActivityChange(qc, dealId, crmCompanyId),
	});
}
